import { CustomerInfo, ItemInfo } from "@redotech/redo-coverage-common";
import { generateCartHash } from "@redotech/redo-coverage-common/coverage-ml/generate-cart-hash";
import { resolveStagecoachVersioning } from "@redotech/redo-coverage-common/coverage-ml/model/stagecoach";
import { finalSaleReturnsCoverageCalculator } from "@redotech/redo-return-coverage-common/coverage-calculator/final-sale-returns";
import { packageProtectionCoverageCalculator } from "@redotech/redo-return-coverage-common/coverage-calculator/package-protection";
import { returnCoverageCalculator } from "@redotech/redo-return-coverage-common/coverage-calculator/returns";
import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import {
  CoverageInfo,
  WidgetAutocheck,
} from "@redotech/redo-storefront-widget-common/widget";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { isDef } from "@redotech/util/checks";
import { ShopifyExtensionClient } from "../client/shopify-extension-client";
import { StorefrontClient } from "../client/storefront";
import { autocheckToCoverageSettings } from "../eligibility/autocheck-conversion";
import {
  cartItemProperties,
  cartItemUnitPrices,
  getProductInfoFromSource,
} from "../eligibility/returns-coverage-eligibility";
import { log } from "../monitoring/log";
import { datadogLog } from "../monitoring/monitor";
import { coveragePriceCache } from "./checkout-buttons/coverage-price-cache";
import { loadCheckoutProbabilityFromSessionStorage } from "./checkout-probability";
import { selectBetaDraw } from "./select-beta-draw";

async function cartItemToItemInfo({
  cartItem,
  autocheck,
  shopifyClient,
  redoClient,
  onTapcart,
  storefrontGraphQLClient,
}: {
  cartItem: ShopifyCartItem;
  autocheck: WidgetAutocheck;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
  storefrontGraphQLClient?: StorefrontClient;
}): Promise<ItemInfo> {
  const { tags, collections, variantMetafields } =
    await getProductInfoFromSource({
      cartItem,
      autocheck,
      shopifyClient,
      redoClient,
      storefrontGraphQLClient,
    });

  const properties = cartItemProperties(cartItem);
  const { price, preDiscountPrice } = cartItemUnitPrices(cartItem, onTapcart);

  return {
    lineItemId: cartItem.id?.toString() ?? "",
    productId: cartItem.product_id?.toString(),
    price,
    preDiscountPrice,
    quantity: cartItem.quantity,
    collections: collections.map((c) => c.title),
    productTags: tags,
    properties,
    vendor: cartItem.vendor || "",
    sku: cartItem.sku,
    discounts: cartItem.discounts?.map((d) => ({ name: d.title })) || [],
    isFinalSale: false,
    hasSubscription: !!cartItem.selling_plan_allocation?.selling_plan?.id,
    deliveryProfileId: null,
    requiresShipping: cartItem.requires_shipping ?? true,
    variantMetafields,
  };
}

function convertToCents({
  dollars,
  onTapcart,
}: {
  dollars: number | undefined;
  onTapcart: boolean;
}) {
  if (!dollars) {
    return 0;
  }

  // tapcart money stays in dollars.
  if (onTapcart) {
    return dollars;
  }

  // round to nearest cent
  return Math.round(dollars * 100);
}

export async function getCoverageInfoFromCoverageCalculators({
  autocheck,
  customerInfo,
  cart,
  shopifyClient,
  redoClient,
  onTapcart,
  storefrontGraphQLClient,
}: {
  autocheck: WidgetAutocheck;
  customerInfo: CustomerInfo;
  cart: ShopifyCart;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
  storefrontGraphQLClient?: StorefrontClient;
}) {
  // Select a per-shopper beta draw from the posterior subsample (if stagecoach is active)
  const mlModel = autocheck?.packageProtection?.coverageMlModel;
  if (mlModel?.id === "stagecoach") {
    const stagecoachConfig = mlModel.config?.stagecoachInferenceConfig;
    if (stagecoachConfig?.posteriorSubsample) {
      const draw = selectBetaDraw({
        posteriorSubsample: stagecoachConfig.posteriorSubsample,
        modelVersionId: stagecoachConfig.modelVersionId,
      });
      // Mutates stagecoachConfig in-place on the cached autocheck object.
      // This is safe because selectBetaDraw is idempotent (persisted in localStorage),
      // and no other code reads betaVector/betaSampleIndex from the autocheck cache.
      if (draw) {
        stagecoachConfig.betaSampleIndex = draw.betaSampleIndex;
        stagecoachConfig.betaVector = draw.betaVector;
      }
    }
  }

  const teamSettings = autocheckToCoverageSettings(autocheck);

  const nonRedoItems = cart.items.filter(
    (item) =>
      !isCoverageFeeProduct({
        product: {
          vendor: item.vendor,
          sku: item.sku,
          title: item.title,
          product_id: item.product_id?.toString(),
        },
        redoCoverageProductIds: new Set(autocheck.coverageProductIds ?? []),
        matchType: CoverageFeeProductMatchType.REDO_ONLY,
      }),
  );
  const items = await Promise.all(
    nonRedoItems.map((item) =>
      cartItemToItemInfo({
        cartItem: item,
        autocheck,
        shopifyClient,
        redoClient,
        onTapcart,
        storefrontGraphQLClient,
      }),
    ),
  );

  const cachedCoveragePrices = coveragePriceCache.getCachedCoveragePrices({
    cartItems: cart.items,
    coverageProductIds: autocheck.coverageProductIds,
  });

  const checkoutProbability = (() => {
    const stored = loadCheckoutProbabilityFromSessionStorage();
    if (isDef(stored?.cumulativeConversionProbability)) {
      // Convert from 0-1 scale to 0-100 scale
      return stored.cumulativeConversionProbability * 100;
    }
    return undefined;
  })();

  const returnCoverageInfo = returnCoverageCalculator.getCoverage({
    team: teamSettings,
    customerInfo,
    items,
    commentsoldIntegration: null,
    ignoreMerchantPaidForEnablementCheck: false,
    redoCoverageProductIds: autocheck.coverageProductIds,
    coveragePausedDates: autocheck.coveragePausedDates ?? null,
  });
  const packageProtectionCoverageInfo =
    packageProtectionCoverageCalculator.getCoverage({
      team: teamSettings,
      customerInfo,
      items,
      commentsoldIntegration: null,
      ignoreMerchantPaidForEnablementCheck: false,
      priceOverrideForCoverageMlModelCacheOnly:
        cachedCoveragePrices?.packageProtection,
      priceCalculationContext: { checkoutProbability },
      redoCoverageProductIds: autocheck.coverageProductIds,
      coveragePausedDates: autocheck.coveragePausedDates ?? null,
    });
  const finalSaleReturnsCoverageInfo =
    finalSaleReturnsCoverageCalculator.getCoverage({
      team: teamSettings,
      customerInfo,
      items,
      commentsoldIntegration: null,
      ignoreMerchantPaidForEnablementCheck: false,
      redoCoverageProductIds: autocheck.coverageProductIds,
      coveragePausedDates: autocheck.coveragePausedDates ?? null,
    });

  coveragePriceCache.maybeCacheCoveragePrices({
    cartItems: cart.items,
    coveragePrices: {
      returns: returnCoverageInfo.priceResult,
      packageProtection: packageProtectionCoverageInfo.priceResult,
      finalSaleReturns: finalSaleReturnsCoverageInfo.priceResult,
    },
    coverageProductIds: autocheck.coverageProductIds,
  });

  const returnPrice = convertToCents({
    dollars: returnCoverageInfo.priceResult?.price,
    onTapcart,
  });
  const packageProtectionPrice = convertToCents({
    dollars: packageProtectionCoverageInfo.priceResult?.price,
    onTapcart,
  });
  const finalSaleReturnsPrice = convertToCents({
    dollars: finalSaleReturnsCoverageInfo.priceResult?.price,
    onTapcart,
  });

  // return coverage is included in final sale coverage. So we need to take the max of the two.
  const returnCoveragePrice = Math.max(returnPrice, finalSaleReturnsPrice);

  const packageProtectionMerchantPaid =
    packageProtectionCoverageInfo.eligible &&
    packageProtectionCoverageInfo.payer === "merchant";
  const packageProtectionEligible =
    packageProtectionCoverageInfo.eligible &&
    packageProtectionCoverageInfo.payer !== "merchant";
  const bothEligible =
    (returnCoverageInfo.eligible || finalSaleReturnsCoverageInfo.eligible) &&
    (packageProtectionEligible || packageProtectionMerchantPaid);
  const bothPrice = packageProtectionMerchantPaid
    ? returnCoveragePrice
    : packageProtectionPrice + returnCoveragePrice;
  const coverageInfo: CoverageInfo = {
    returns: {
      cartEligible:
        returnCoverageInfo.eligible || finalSaleReturnsCoverageInfo.eligible,
      price: returnCoveragePrice,
      isFinalSale: finalSaleReturnsCoverageInfo.eligible,
      reasonIneligible: returnCoverageInfo.reasonIneligible,
    },
    packageProtection: {
      cartEligible: packageProtectionEligible,
      price: packageProtectionEligible ? packageProtectionPrice : 0,
      reasonIneligible: packageProtectionCoverageInfo.reasonIneligible,
    },
    both: {
      cartEligible: bothEligible,
      price: bothEligible ? bothPrice : 0,
      reasonIneligible: bothEligible
        ? undefined
        : returnCoverageInfo.reasonIneligible ||
          packageProtectionCoverageInfo.reasonIneligible,
    },
  };

  // Map ShopifyCartItem (variant_id) to HashableCartItem (variantId)
  // to match the format generateCartHash expects
  const cartHash = generateCartHash(
    cart.items
      .map((item) => ({
        variantId: parseNumericIdFromShopifyId(item.variant_id.toString()),
        productId: item.product_id?.toString(),
        sku: undefined,
        vendor: item.vendor ?? undefined,
        name: item.title ?? undefined,
        quantity: item.quantity,
      }))
      .filter((v) => v.variantId && v.quantity > 0),
    new Set(autocheck.coverageProductIds ?? []),
  );

  const isStagecoachModelActive = mlModel?.id === "stagecoach";
  const ppPriceResult = packageProtectionCoverageInfo.priceResult;
  const cachedPP = cachedCoveragePrices?.packageProtection;
  // Resolve versioning: inference result → cache → shadow-mode config fallback
  const resolvedVersioning = resolveStagecoachVersioning({
    inferenceResult: {
      modelVersionId: ppPriceResult?.modelVersionId ?? cachedPP?.modelVersionId,
      betaSampleIndex:
        ppPriceResult?.betaSampleIndex ?? cachedPP?.betaSampleIndex,
    },
    mlModel,
  });
  const stagecoachModelMetadata =
    isStagecoachModelActive && resolvedVersioning
      ? {
          modelVersionId: resolvedVersioning.modelVersionId,
          betaSampleIndex: resolvedVersioning.betaSampleIndex,
        }
      : undefined;

  const validCartItems = cart.items.filter(
    (item) =>
      returnCoverageInfo.eligibleItems.some(
        (eligibleItem) => eligibleItem.lineItemId === item.id?.toString(),
      ) ||
      packageProtectionCoverageInfo.eligibleItems.some(
        (eligibleItem) => eligibleItem.lineItemId === item.id?.toString(),
      ) ||
      finalSaleReturnsCoverageInfo.eligibleItems.some(
        (eligibleItem) => eligibleItem.lineItemId === item.id?.toString(),
      ),
  );

  return { coverageInfo, stagecoachModelMetadata, cartHash, validCartItems };
}

export function compareCoverageInfos({
  oldCoverageInfo,
  newCoverageInfo,
  autocheck,
  cart,
  onTapcart,
}: {
  oldCoverageInfo: CoverageInfo;
  newCoverageInfo: CoverageInfo;
  autocheck: WidgetAutocheck;
  cart: ShopifyCart;
  onTapcart: boolean;
}) {
  const ineligibleReasons = {
    returns: newCoverageInfo.returns.reasonIneligible,
    packageProtection: newCoverageInfo.packageProtection.reasonIneligible,
    both: newCoverageInfo.both.reasonIneligible,
  };

  // remove reasonIneligible from newCoverageInfo to avoid comparing the reasonIneligible strings
  newCoverageInfo.returns.reasonIneligible = undefined;
  newCoverageInfo.packageProtection.reasonIneligible = undefined;
  newCoverageInfo.both.reasonIneligible = undefined;

  // Tapcart prices are in dollars; non-Tapcart prices are in cents
  const priceThreshold = onTapcart ? 0.02 : 2;
  const pricesDifferSignificantly =
    Math.abs(oldCoverageInfo.returns.price - newCoverageInfo.returns.price) >
      priceThreshold ||
    Math.abs(
      oldCoverageInfo.packageProtection.price -
        newCoverageInfo.packageProtection.price,
    ) > priceThreshold ||
    Math.abs(oldCoverageInfo.both.price - newCoverageInfo.both.price) >
      priceThreshold;

  const eligibilityDiffers =
    oldCoverageInfo.returns.cartEligible !==
      newCoverageInfo.returns.cartEligible ||
    oldCoverageInfo.returns.isFinalSale !==
      newCoverageInfo.returns.isFinalSale ||
    oldCoverageInfo.packageProtection.cartEligible !==
      newCoverageInfo.packageProtection.cartEligible ||
    oldCoverageInfo.both.cartEligible !== newCoverageInfo.both.cartEligible;

  if (pricesDifferSignificantly || eligibilityDiffers) {
    log("Coverage infos are different", {
      oldCoverageInfo,
      newCoverageInfo,
      ineligibleReasons,
    });
    datadogLog("info", "Coverage infos are different", {
      oldCoverageInfo,
      newCoverageInfo,
      lineItems: cart?.items ?? [],
      ineligibleReasons,
      isTapcart: onTapcart,
    });
    return false;
  }

  log("Coverage infos are the same");
  return true;
}
